/**
 * Shared push-settings form. Used by both Admin (platform default) and Tenant
 * (per-firm override). Renders two topic panels: Hearings + Cause-list.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/time";
import { api, apiError } from "@/lib/api";
import { Bell, BellOff, Clock, Hammer, FileText, Loader2, ShieldCheck, ChevronRight } from "lucide-react";

const TOPIC_META = {
    hearing: { icon: Hammer, label: "Hearing reminders",
        sub: "Reminder push before each scheduled hearing." },
    causelist: { icon: FileText, label: "Cause list",
        sub: "Daily digest + optional immediate push when eCourts brings a new entry." },
};

function TopicCard({ topic, value, onChange }) {
    const meta = TOPIC_META[topic];
    const Icon = meta.icon;
    const set = (k, v) => onChange({ ...value, [k]: v });

    return (
        <div className="border border-border bg-card p-4 space-y-4" data-testid={`push-topic-${topic}`}>
            <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 text-primary mt-0.5"/>
                <div className="flex-1">
                    <div className="text-foreground text-sm font-medium">{meta.label}</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">{meta.sub}</div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="accent-primary" data-testid={`push-${topic}-enabled`}
                        checked={!!value.enabled} onChange={e => set("enabled", e.target.checked)}/>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{value.enabled ? "On" : "Off"}</span>
                </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">
                        {topic === "hearing" ? "Daily send time (IST)" : "Daily digest time (IST)"}
                    </label>
                    <input type="time" value={value.send_time || ""} onChange={e => set("send_time", e.target.value)}
                        data-testid={`push-${topic}-time`}
                        disabled={!value.enabled}
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono disabled:opacity-50"/>
                </div>
                {topic === "hearing" && (
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Hours before hearing</label>
                        <input type="number" min="0" max="168" value={value.offset_hours ?? 18}
                            onChange={e => set("offset_hours", Number(e.target.value))}
                            data-testid={`push-${topic}-offset`}
                            disabled={!value.enabled}
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono disabled:opacity-50"/>
                    </div>
                )}
                {topic === "causelist" && (
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Immediate on new sync</label>
                        <label className="flex items-center gap-2 bg-background border border-border px-3 py-2 cursor-pointer disabled:opacity-50">
                            <input type="checkbox" className="accent-primary"
                                checked={!!value.immediate} onChange={e => set("immediate", e.target.checked)}
                                data-testid={`push-${topic}-immediate`}
                                disabled={!value.enabled}/>
                            <span className="text-foreground text-xs">Push the moment a new entry appears</span>
                        </label>
                    </div>
                )}
            </div>

            {topic === "hearing" && (
                <div className="border border-amber-500/30 bg-amber-500/[0.03] p-3 space-y-2"
                     data-testid="push-hearing-urgent-card">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-foreground text-xs font-medium flex items-center gap-1.5">
                                <span className="text-amber-400">⚡</span> Time-sensitive escalation
                            </div>
                            <div className="text-muted-foreground text-[11px] mt-0.5">
                                Fire ONE extra urgent push when a hearing is this close. iOS time-sensitive, Web SOS vibration, bundled CNR/Filing/Item to dial the registrar instantly.
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-foreground text-lg font-mono leading-none">
                                {(() => {
                                    const m = Number(value.urgent_window_minutes ?? 120);
                                    if (m <= 0) return "OFF";
                                    if (m >= 60) {
                                        const h = m / 60;
                                        return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
                                    }
                                    return `${m}m`;
                                })()}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">window</div>
                        </div>
                    </div>
                    <input
                        type="range" min="0" max="480" step="15"
                        value={Number(value.urgent_window_minutes ?? 120)}
                        onChange={e => set("urgent_window_minutes", Number(e.target.value))}
                        disabled={!value.enabled}
                        data-testid="push-hearing-urgent-slider"
                        className="w-full accent-amber-400 disabled:opacity-50"/>
                    <div className="flex flex-wrap gap-1.5">
                        {[
                            { label: "Off", v: 0 },
                            { label: "30m · trial cts", v: 30 },
                            { label: "1h", v: 60 },
                            { label: "2h · default", v: 120 },
                            { label: "4h · High Cts", v: 240 },
                        ].map(p => {
                            const active = Number(value.urgent_window_minutes ?? 120) === p.v;
                            return (
                                <button key={p.v} type="button"
                                    onClick={() => set("urgent_window_minutes", p.v)}
                                    disabled={!value.enabled}
                                    data-testid={`push-hearing-urgent-preset-${p.v}`}
                                    className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 border disabled:opacity-50 ${active ? "border-amber-400 text-amber-300 bg-amber-500/10" : "border-border text-muted-foreground hover:border-amber-400 hover:text-amber-300"}`}>
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Match rule</div>
                <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 border border-border bg-background px-3 py-2 cursor-pointer">
                        <input type="checkbox" className="accent-primary"
                            checked={!!value.match_by_case_number}
                            onChange={e => set("match_by_case_number", e.target.checked)}
                            data-testid={`push-${topic}-match-case`}
                            disabled={!value.enabled}/>
                        <span className="text-foreground text-xs">By case number</span>
                    </label>
                    <label className="flex items-center gap-2 border border-border bg-background px-3 py-2 cursor-pointer">
                        <input type="checkbox" className="accent-primary"
                            checked={!!value.match_by_advocate_name}
                            onChange={e => set("match_by_advocate_name", e.target.checked)}
                            data-testid={`push-${topic}-match-adv`}
                            disabled={!value.enabled}/>
                        <span className="text-foreground text-xs">By advocate name</span>
                    </label>
                </div>
                {!(value.match_by_case_number || value.match_by_advocate_name) && value.enabled && (
                    <div className="text-[11px] text-amber-400 mt-2 border border-amber-500/30 bg-amber-500/5 px-2 py-1.5">
                        Both rules off — only firm owner / managing partner will receive pushes.
                    </div>
                )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-primary"
                    checked={!!value.sticky} onChange={e => set("sticky", e.target.checked)}
                    data-testid={`push-${topic}-sticky`}
                    disabled={!value.enabled}/>
                <span className="text-foreground text-xs">
                    Sticky · cannot be swiped away (tap-Dismiss only)
                </span>
            </label>
        </div>
    );
}

export default function PushSettingsForm({ scope, getEndpoint, putEndpoint, testEndpoint, logsEndpoint, headline }) {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [logs, setLogs] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(getEndpoint);
            setSettings(data.settings);
            const lg = await api.get(logsEndpoint).catch(() => ({ data: [] }));
            setLogs(lg.data || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [getEndpoint]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(putEndpoint, settings);
            toast.success("Push settings saved");
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const testPush = async () => {
        setTesting(true);
        try {
            const { data } = await api.post(testEndpoint);
            const suffix = data.note ? ` · ${data.note}` : "";
            toast.success(`Test logged · ${data.log_id?.slice(-6) || "—"}${suffix}`);
            await new Promise(r => setTimeout(r, 600));
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setTesting(false); }
    };

    if (loading) return <div className="text-muted-foreground text-sm">Loading…</div>;
    if (!settings) return <div className="text-muted-foreground text-sm">No settings.</div>;

    return (
        <div className="space-y-6" data-testid={`push-settings-${scope}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-foreground text-lg">{headline}</h2>
                    <p className="text-muted-foreground text-[12px] mt-1 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3"/>Strict per-tenant isolation · audit logged
                    </p>
                </div>
                <div className="flex gap-2">
                    {testEndpoint && (
                        <button onClick={testPush} disabled={testing} data-testid="push-test-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                            {testing ? <Loader2 className="w-3 h-3 animate-spin"/> : <Bell className="w-3 h-3"/>}Test push
                        </button>
                    )}
                    <button onClick={save} disabled={saving} data-testid="push-save-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : null}Save
                    </button>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <TopicCard topic="hearing" value={settings.hearing}
                    onChange={(v) => setSettings({ ...settings, hearing: v })}/>
                <TopicCard topic="causelist" value={settings.causelist}
                    onChange={(v) => setSettings({ ...settings, causelist: v })}/>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-foreground text-sm">Recent push log</h3>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{logs.length} entries</span>
                </div>
                <div className="border border-border bg-card overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            <tr className="border-b border-border">
                                <th className="text-left px-3 py-2">When</th>
                                <th className="text-left px-3 py-2">Topic</th>
                                <th className="text-left px-3 py-2">Title</th>
                                <th className="text-left px-3 py-2 hidden md:table-cell">Matched by</th>
                                <th className="text-left px-3 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 && (
                                <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No pushes yet.</td></tr>
                            )}
                            {logs.map(l => (
                                <tr key={l.id} className="border-b border-card" data-testid={`push-log-row-${l.id}`}>
                                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(l.created_at)}</td>
                                    <td className="px-3 py-2 text-foreground capitalize">{l.topic}</td>
                                    <td className="px-3 py-2 text-foreground">
                                        <div className="font-medium">{l.title}</div>
                                        <div className="text-muted-foreground text-[11px]">{l.body}</div>
                                    </td>
                                    <td className="px-3 py-2 text-muted-foreground hidden md:table-cell">{l.matched_by}</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${
                                            l.status === "sent" ? "border-emerald-500/40 text-emerald-400" :
                                            l.status === "dismissed" ? "border-blue-500/40 text-blue-400" :
                                            l.status === "failed" ? "border-rose-500/40 text-rose-400" :
                                            "border-border text-muted-foreground"
                                        }`}>{l.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
